import { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";
import { Play, Pause } from "lucide-react";

const FALLBACK = [
  { name: "Acoustic Pop", snippet: "\"…and the rain on Brigade Road knew your name before I did…\"" },
  { name: "Soul / R&B", snippet: "\"…every hoodie you stole became a verse I had to sing…\"" },
  { name: "Indie Folk", snippet: "\"…we planted our story where the Goa tides turn…\"" },
  { name: "Cinematic Ballad", snippet: "\"…Ananya, the stars learned to spell you…\"" },
  { name: "Upbeat Party", snippet: "\"…slow mornings, your laugh on loop, chai gone cold…\"" },
];

export default function Samples({ content }) {
  const genres = content?.genres?.length ? content.genres : FALLBACK;
  const [active, setActive] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [sec, setSec] = useState(0);
  const timer = useRef(null);

  useEffect(() => {
    if (playing) timer.current = setInterval(() => setSec((s) => (s + 1) % 168), 1000);
    else clearInterval(timer.current);
    return () => clearInterval(timer.current);
  }, [playing]);

  useEffect(() => { setActive(0); setPlaying(false); setSec(0); }, [genres]);

  const genre = genres[active] || FALLBACK[0];
  const fmt = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;

  return (
    <section id="samples" className="py-20 sm:py-28 scroll-mt-24 bg-cardalt border-y-[3px] border-ink" data-testid="samples-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <motion.div initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }}>
          <p className="font-mono text-xs uppercase tracking-[0.25em] text-pink font-semibold mb-4">Listen first</p>
          <h2 className="font-display font-bold text-4xl sm:text-5xl tracking-tight leading-[1.1] max-w-2xl">
            Hear what <span className="text-grape">custom magic</span> sounds like
          </h2>
          <p className="mt-4 text-plum font-medium max-w-xl text-sm sm:text-base">
            Sample previews below — yours is written from scratch, from your story.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 32 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7, delay: 0.1 }}
          className="mt-12 rounded-[2rem] bg-white border-[3px] border-ink shadow-[8px_8px_0_#2D1A32] p-6 sm:p-10"
        >
          <div className="flex flex-wrap gap-2.5 mb-8" data-testid="genre-tabs">
            {genres.map((g, i) => (
              <button
                key={g.name + i}
                data-testid={`genre-tab-${g.name.toLowerCase().replace(/[\s/]+/g, "-")}`}
                onClick={() => { setActive(i); setSec(0); }}
                className={`btn-pop rounded-full px-5 py-2 text-xs font-head font-bold border-2 border-ink ${i === active ? "bg-pink text-white shadow-[3px_3px_0_#2D1A32]" : "bg-cream text-plum"}`}
              >
                {g.name}
              </button>
            ))}
          </div>

          <div className="flex flex-col sm:flex-row items-center gap-6">
            <button
              data-testid="sample-play-btn"
              onClick={() => setPlaying((p) => !p)}
              className="btn-pop w-16 h-16 shrink-0 rounded-full bg-pink text-white border-[3px] border-ink shadow-[4px_4px_0_#2D1A32] flex items-center justify-center"
              aria-label={playing ? "Pause sample" : "Play sample"}
            >
              {playing ? <Pause size={24} fill="currentColor" /> : <Play size={24} fill="currentColor" className="ml-1" />}
            </button>
            <div className="flex-1 w-full">
              <div className="flex items-end gap-[3px] h-16" aria-hidden="true">
                {[...Array(48)].map((_, i) => (
                  <span
                    key={i}
                    className={`flex-1 rounded-full ${i % 3 === 0 ? "bg-pink" : i % 3 === 1 ? "bg-sunny" : "bg-grape"} ${playing ? "eq-bar" : "scale-y-[0.3] opacity-30"}`}
                    style={{ height: `${25 + 70 * Math.abs(Math.sin(i * 0.9 + genre.name.length))}%`, animationDelay: `${i * 0.05}s` }}
                  />
                ))}
              </div>
              <div className="mt-3 flex justify-between font-mono text-[11px] text-lilac font-medium">
                <span data-testid="sample-track-time">{fmt(sec)}</span>
                <span>{genre.name} · sample</span>
                <span>2:48</span>
              </div>
            </div>
          </div>

          <div className="mt-8 mx-auto max-w-xl rounded-2xl bg-sunny/40 border-2 border-ink p-5 rotate-[-1deg]" data-testid="sample-lyric-snippet">
            <p className="font-display font-medium text-lg sm:text-xl text-ink text-center">{genre.snippet}</p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
